/*
 * Copyright (c) 2025 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback} from 'react';
import {withTranslation} from 'react-i18next';
import {makeStyles} from '@material-ui/core/styles';
import Tooltip from '@material-ui/core/Tooltip';
import {translate} from '@ion/core/src/locale/locale';
import {WarningIcon} from '@ion/dls/src/icons/WarningIcon';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {setShowNotification} from './store';
import useEnvironmentDetails from './useEnvironmentDetails';

/**
 * Defines the properties for the EnvironmentIndicator component.
 *
 * @see {@link EnvironmentIndicator}
 */
export type EnvironmentIndicatorProps = {
  collapsed?: boolean
};

// defines the style definitions for the `EnvironmentIndicator` component ...
const useStyles = makeStyles((theme) => ({
  container : {
    display    : 'flex',
    alignItems : 'center',
    minWidth   : '160px',
    gap        : theme.spacing(1),
    cursor     : 'pointer'
  },
  containerCollapsed : {
    display        : 'flex',
    alignItems     : 'center',
    justifyContent : 'center',
    minWidth       : 'auto',
    cursor         : 'pointer'
  },
  message : {
    margin : 0,
    color  : dlsColor('caRed')
  },
  icon : {
    display : 'flex'
  },
  tooltip : {
    alignItems      : 'center',
    backgroundColor : dlsColor('white'),
    border          : `1px solid ${dlsColor('grey5')}`,
    borderRadius    : 8,
    boxShadow       : '0 2px 8px rgba(0, 0, 0, 0.12)',
    boxSizing       : 'border-box',
    color           : dlsColor('caRed'),
    display         : 'flex',
    fontFamily      : 'Inter',
    fontSize        : 14,
    fontWeight      : 500,
    height          : 32,
    lineHeight      : '17px',
    padding         : '4px 16px',
    whiteSpace      : 'nowrap'
  }
}));

/**
 * The `EnvironmentIndicator` component provides a warning indicator which
 * displays a warning icon and environment details when an environment
 * configuration is loaded from an external domain. When clicked, the
 * `EnvironmentNotification` dialog will be displayed.
 *
 * ```typescript
 *   import EnvironmentIndicator from '@ion/applications/src/env/EnvironmentIndicator';
 *   ...
 *
 *   <EnvironmentIndicator />
 * ```
 *
 * The optional `collapsed` property renders the indicator as the warning icon
 * alone, with the environment details moved into a tooltip. This is intended for
 * hosts such as a collapsed side navigation rail, which is too narrow for the
 * default inline label.
 *
 * ```typescript
 *   <EnvironmentIndicator collapsed />
 * ```
 */
const EnvironmentIndicator = ({collapsed = false}: EnvironmentIndicatorProps) => {
  const classes = useStyles();
  const config  = useEnvironmentDetails();

  const onIndicatorClick = useCallback(() => {
    setShowNotification(true);
  }, []);

  if (config.isExternal) {
    const label = translate('notifications:environment.environment', {remote: config.remote});
    if (collapsed) {
      return (
        <Tooltip title={<span className={classes.tooltip}>{label}</span>}
          placement='right'
          enterDelay={0}
          leaveDelay={0}>
          <div className={classes.containerCollapsed}
            aria-label={label}
            onClick={onIndicatorClick}>
            <div className={classes.icon}>
              <WarningIcon />
            </div>
          </div>
        </Tooltip>
      );
    }
    return (
      <div className={classes.container} onClick={onIndicatorClick}>
        <div className={classes.icon}>
          <WarningIcon />
        </div>
        <p className={classes.message}>
          {label}
        </p>
      </div>
    );
  }
  return null;
};
export default withTranslation([
  'notifications'
])(React.memo(EnvironmentIndicator));
